/**
 * The New requests chip (Corbin approved 2026-10-28, mockup
 * https://claude.ai/artifact/98SqJMh6FSCfJHEvxopBQT). Shown instead of the
 * booking chip for people with "Try New requests" on.
 *
 * It counts New + Trying to reach from the ONE list (GET /api/requests), the
 * same rows the page shows in those two lanes. A tap opens the page; there is
 * no flyout. Grey at zero, blue when new, amber at half the promise, red when
 * one is late. It glows ~5 s once per new request (per person). No bell.
 *
 * Card-hold rows are not requests (pick 2A): they live in Close Out.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Phone } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { REQUESTS_QUERY_KEY, type RequestsView } from "@/lib/requests";

const SEEN_LIMIT = 300;
const seenKey = (userId: string | undefined) => `gwc.requests.seen.${userId ?? "anon"}`;
function readSeen(userId: string | undefined): Set<string> {
    try {
        const raw = localStorage.getItem(seenKey(userId));
        return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
        return new Set();
    }
}
function writeSeen(userId: string | undefined, keys: Set<string>) {
    try {
        localStorage.setItem(seenKey(userId), JSON.stringify([...keys].slice(-SEEN_LIMIT)));
    } catch {
        // private window: the glow just repeats next load
    }
}

export function useRequests(enabled = true) {
    return useQuery({
        queryKey: REQUESTS_QUERY_KEY,
        queryFn: () => api.get<RequestsView>("/api/requests"),
        enabled,
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
        staleTime: 15_000,
        retry: false,
    });
}

export function RequestsChip() {
    const { can, user } = useAuth();
    const enabled = can(PERMISSIONS.jobs.schedule);
    const navigate = useNavigate();
    const q = useRequests(enabled);
    const [glow, setGlow] = useState(false);
    const timer = useRef<number | null>(null);
    const userId = user?.id;
    const keys = useMemo(
        () => (q.data?.items ?? []).filter((i) => i.stage === "new" || i.stage === "trying").map((i) => i.id),
        [q.data],
    );
    useEffect(() => {
        if (!q.data) return;
        const seen = readSeen(userId);
        const fresh = keys.filter((k) => !seen.has(k));
        if (fresh.length === 0) return;
        for (const k of fresh) seen.add(k);
        writeSeen(userId, seen);
        setGlow(false);
        window.requestAnimationFrame(() => setGlow(true));
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setGlow(false), 5100);
    }, [keys, q.data, userId]);
    useEffect(() => () => (timer.current ? window.clearTimeout(timer.current) : undefined), []);

    if (!enabled) return null;
    const pill = q.data?.pill;
    const tone = q.isError ? "none" : (pill?.tone ?? "none");
    const label = q.isError ? "New requests" : (pill?.label ?? "No new requests");
    return (
        <button
            type="button"
            className={`bkchip t-${tone}${glow ? " glow" : ""}`}
            aria-label={`New requests: ${label}. Opens the list.`}
            data-testid="requests-chip"
            onClick={() => navigate("/requests")}
            title={q.isError ? "Couldn't load new requests" : undefined}
        >
            <Phone size={16} aria-hidden />
            <span className="c">{label}</span>
        </button>
    );
}
